import { getFeatures } from '@/apis/features';
import {
  accountLookUp,
  fetchPasswordPolicy,
  fetchUsernamePolicy,
  registerUser,
  requestOtp,
  secretWord,
  verifyOtp,
} from '@/apis/etiqaPlus';

import {
  getBankList,
  getCountryOfResidence,
  getIndustryList,
  getNationalityList,
  getOccupationList,
  getSourceOfFundList,
} from '@/apis/options';
import { getPaymentType } from '@/apis/payment';
import {
  getProductArea,
  getProductPlanClass,
  getProductPlanType,
  getTravelPlanPrice,
} from '@/apis/product';
import { Lookup } from '@/enums/enum/Lookup';
import { UiState } from '@/types/Common/UiState';
import { ApiResponseWrapper } from '@/types/Common/Wrapper';
import { LookUpResponse, PasswordPolicy, UsernamePolicy } from '@/types/EtiqaPlus';
import { FeatureConfig } from '@/types/Features';
import {
  BankResponse,
  IndustryResponse,
  NationalityResponse,
  OccupationResponse,
  SourceOfFundResponse,
} from '@/types/Options';
import { CountryOfResidenceResponse } from '@/types/Options/CountryOfResidenceResponse';
import {
  PaymentType,
  PaymentTypeResponseWrapper,
} from '@/types/Payment/PaymentType';
import { ProductPlanPriceResponse } from '@/types/PriceDetail';
import {
  ProductAreaResponse,
  ProductPlanClassResponse,
  ProductPlanTypeResponse,
} from '@/types/Product';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

// initial state uses class instance
const initialState: UiState = {
  language: 'en',
  productArea: undefined,
  productPlanClass: undefined,
  bankList: undefined,
  payModes: {
    data: [],
    loading: false,
    errors: [],
    succeeded: false,
    message: '',
  },
  loading: false,
  error: false,
  showModal: false,
  mobile: {
    hiddenPlan: 0,
  },
};

export const uiStateSlice = createSlice({
  name: 'uiState',
  initialState,
  reducers: {
    setMobileHiddenPlan: (state, action: PayloadAction<number>) => {
      state.mobile.hiddenPlan = action.payload;
    },
    setShowModal: (state, action: PayloadAction<boolean>) => {
      state.showModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductArea.pending, (state, action) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getProductArea.fulfilled, (state, action) => {
        if (action.meta.arg.language !== '') {
          state.productArea = action.payload.data as ProductAreaResponse[];
          state.loading = false;
          state.error = false;
        }
      })
      .addCase(getProductArea.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getProductPlanClass.pending, (state, action) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getProductPlanClass.fulfilled, (state, action) => {
        if (action.meta.arg.language !== '') {
          state.productPlanClass = action.payload
            .data as ProductPlanClassResponse[];
          state.loading = false;
          state.error = false;
        }
      })
      .addCase(getProductPlanClass.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getProductPlanType.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getProductPlanType.fulfilled, (state, action) => {
        if (action.meta.arg.language !== '') {
          state.productPlanType = action.payload
            .data as ProductPlanTypeResponse[];
          state.loading = false;
          state.error = false;
        }
      })
      .addCase(getProductPlanType.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getTravelPlanPrice.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getTravelPlanPrice.fulfilled, (state, action) => {
        const response = action.payload.data as ProductPlanPriceResponse;
        state.productPremiumRate = response.plan;
        state.productAddonRate = response.addOn;
        state.loading = false;
        state.error = false;
      })
      .addCase(getTravelPlanPrice.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getBankList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getBankList.fulfilled, (state, action) => {
        state.bankList = action.payload.data as BankResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getBankList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getNationalityList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getNationalityList.fulfilled, (state, action) => {
        state.nationalityList = action.payload.data as NationalityResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getNationalityList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getOccupationList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getOccupationList.fulfilled, (state, action) => {
        state.occupationList = action.payload.data as OccupationResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getOccupationList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getIndustryList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getIndustryList.fulfilled, (state, action) => {
        state.industryList = action.payload.data as IndustryResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getIndustryList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getCountryOfResidence.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getCountryOfResidence.fulfilled, (state, action) => {
        state.countryOfResidence = action.payload
          .data as CountryOfResidenceResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getCountryOfResidence.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getSourceOfFundList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getSourceOfFundList.fulfilled, (state, action) => {
        state.sourceOfFundList = action.payload.data as SourceOfFundResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getSourceOfFundList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getPaymentType.pending, (state) => {
        state.payModes.loading = true;
      })
      .addCase(getPaymentType.fulfilled, (state, action) => {
        state.payModes = action.payload;
        state.payModes.loading = false;
      })
      .addCase(getPaymentType.rejected, (state, action) => {
        state.payModes = action.payload as PaymentTypeResponseWrapper<
          PaymentType[]
        >;
        state.payModes.loading = false;
      });

    builder
      .addCase(fetchUsernamePolicy.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(
        fetchUsernamePolicy.fulfilled,
        (
          state,
          action: PayloadAction<ApiResponseWrapper<{ data: UsernamePolicy }>>,
        ) => {
          state.usernamePolicy = action.payload.data.data;
          state.loading = false;
          state.error = false;
        },
      )
      .addCase(fetchUsernamePolicy.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(fetchPasswordPolicy.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(
        fetchPasswordPolicy.fulfilled,
        (
          state,
          action: PayloadAction<
            ApiResponseWrapper<{
              data: PasswordPolicy;
            }>
          >,
        ) => {
          state.passwordPolicy = action.payload.data.data;
          state.loading = false;
          state.error = false;
        },
      )
      .addCase(fetchPasswordPolicy.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(accountLookUp.pending, (state) => {
        state.error = false;
      })
      .addCase(accountLookUp.fulfilled, (state, action) => {
        state.lookUpNew = action.payload;
        state.usernameExist = !!action.meta.arg.username && (action.payload as LookUpResponse).code === Lookup.RecordFound;
        state.error = false;
      })
      .addCase(accountLookUp.rejected, (state) => {
        state.error = true;
      });

    builder
      .addCase(requestOtp.pending, (state) => {
        state.requestOtploading = true;
        state.error = false;
      })
      .addCase(requestOtp.fulfilled, (state, action) => {
        state.requestOtp = action.payload;
        state.requestOtploading = false;
        state.error = false;
      })
      .addCase(requestOtp.rejected, (state) => {
        state.requestOtploading = false;
        state.error = true;
      });

    builder
      .addCase(verifyOtp.pending, (state) => {
        state.verifyOtploading = true;
        state.error = false;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.verifyOtp = action.payload;
        state.verifyOtploading = false;
        state.error = false;
      })
      .addCase(verifyOtp.rejected, (state) => {
        state.verifyOtploading = false;
        state.error = true;
      });

    builder
      .addCase(registerUser.pending, (state) => {
        state.registerloading = true;
        state.error = false;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.register = action.payload;
        state.registerloading = false;
        state.error = false;
      })
      .addCase(registerUser.rejected, (state) => {
        state.registerloading = false;
        state.error = true;
      });

    builder
      .addCase(secretWord.pending, (state) => {
        state.secretWordloading = true;
        state.error = false;
      })
      .addCase(secretWord.fulfilled, (state, action) => {
        state.secretWord = action.payload;
        state.secretWordloading = false;
        state.error = false;
      })
      .addCase(secretWord.rejected, (state) => {
        state.secretWordloading = false;
      });

    builder
      .addCase(getFeatures.pending, (state) => {
        state.error = false;
      })
      .addCase(getFeatures.fulfilled, (state, action) => {
        state.features = action.payload as FeatureConfig;
        state.error = false;
      })
      .addCase(getFeatures.rejected, (state) => {
        state.error = true;
      });
  },
});

export const { setMobileHiddenPlan, setShowModal } = uiStateSlice.actions;
export default uiStateSlice.reducer;
